import { bobHazardsAt } from '../../../packages/domain/src/68-bob-bounce.mjs';
import { bobMeshFactory } from './75-bob-meshes.mjs';
import { createBobCrateArt } from './149-bob-crate-art.mjs';

function pickup(THREE, factory, kind) {
  const group = new THREE.Group();
  const shape = new THREE.Shape();
  const vertices =
    kind === 'boost'
      ? [
          [-0.03, 0.44],
          [-0.29, -0.03],
          [0, -0.03],
          [-0.1, -0.44],
          [0.3, 0.11],
          [0.04, 0.11],
          [0.17, 0.44],
        ]
      : [
          [-0.32, 0.34],
          [0.32, 0.34],
          [0.29, -0.05],
          [0, -0.43],
          [-0.29, -0.05],
        ];
  vertices.forEach(([x, y], i) => (i ? shape.lineTo(x, y) : shape.moveTo(x, y)));
  shape.closePath();
  const geometry = factory.keep(
    new THREE.ExtrudeGeometry(shape, {
      depth: 0.08,
      bevelEnabled: true,
      bevelSize: 0.025,
      bevelThickness: 0.025,
      bevelSegments: 2,
      steps: 1,
    }),
  );
  const icon = new THREE.Mesh(
    geometry,
    factory.material(kind === 'boost' ? '#ffe16c' : '#65caff', 0.23),
  );
  icon.position.z = -0.05;
  group.add(icon);
  if (kind === 'shield') {
    factory.box(group, '#eafcff', [0, 0.01, -0.085], [0.075, 0.33, 0.035]);
    factory.box(group, '#eafcff', [0, 0.02, -0.085], [0.23, 0.07, 0.035]);
  }
  factory.torus(group, kind === 'boost' ? '#ffd36d' : '#92dbff', [0, 0, 0.05], 0.57, 0.2);
  return group;
}

function checkpoint(THREE, factory, gate, bounds) {
  const group = new THREE.Group();
  group.name = `checkpoint:${gate.id}`;
  group.position.z = gate.z;
  const left = gate.x - gate.width / 2,
    right = gate.x + gate.width / 2;
  // Low striped rails communicate the required opening without hiding the route.
  for (const [a, b] of [
    [bounds.minX, left],
    [right, bounds.maxX],
  ]) {
    const barrier = factory.box(group, '#756289', [(a + b) / 2, 0.3, 0], [b - a, 0.6, 0.35]);
    barrier.name = `Required gate barrier ${a === bounds.minX ? 'left' : 'right'}`;
    for (let x = a + 0.24; x < b; x += 0.55)
      factory.box(group, '#ffce79', [x, 0.32, -0.2], [0.17, 0.55, 0.045]);
  }
  for (const x of [left - 0.07, right + 0.07]) {
    factory.box(group, '#56d8d1', [x, 3, 0], [0.18, 6, 0.2], 0.3);
    factory.box(group, '#e9fff0', [x, 3, -0.13], [0.045, 5.8, 0.035], 0.2);
  }
  factory.box(group, '#56d8d1', [gate.x, 6, 0], [gate.width + 0.28, 0.14, 0.2], 0.3);
  const arrow = new THREE.Group();
  for (const side of [-1, 1]) {
    const arm = factory.box(arrow, '#fff6a5', [side * 0.18, 0, 0], [0.12, 0.56, 0.09], 0.22);
    arm.rotation.z = (side * Math.PI) / 4;
  }
  arrow.position.set(gate.x, 5.2, -0.16);
  group.add(arrow);
  return group;
}

/** Disposable spatial window for long courses. The visible interval extends
 * behind the camera and beyond the fog horizon. Rebuilding at24m boundaries
 * preserves canonical IDs/positions; it never spawns gameplay or changes state. */
export function createBobCourse(
  THREE,
  {
    scene,
    level,
    palette,
    prepareMaterial,
    onInvalidate = () => {},
    createCrateArt = createBobCrateArt,
  },
) {
  const crateArt = createCrateArt({ THREE, prepareMaterial, onInvalidate });
  let detachCrates = [];
  let factory = null,
    root = null,
    segment = null;
  let foods = [],
    hazards = [],
    powers = [],
    crates = [],
    gates = [],
    springs = [],
    shield = null,
    shieldRim = null,
    boost = null,
    gateMeshes = [];
  let disposed = false;
  function clear() {
    for (const detach of detachCrates) detach();
    detachCrates = [];
    if (root) scene.remove(root);
    factory?.dispose();
    root = null;
    factory = null;
    foods = [];
    hazards = [];
    powers = [];
    crates = [];
    gates = [];
    springs = [];
    gateMeshes = [];
  }
  function build(nextSegment) {
    clear();
    segment = nextSegment;
    factory = bobMeshFactory(THREE, { prepareMaterial });
    root = new THREE.Group();
    root.name = 'Visible course window';
    scene.add(root);
    const start = segment * 24 - 24,
      end = segment * 24 + 96;
    const inView = (item) =>
      item.z >= start - (item.depth || 0) && item.z <= end + (item.depth || 0);
    const width = level.bounds.maxX - level.bounds.minX;
    const city = level.theme.includes('city') || level.theme === 'celestial-palace';
    const edgeColour = city
      ? '#d6ccc2'
      : level.theme === 'golden-desert'
        ? '#e7c596'
        : level.theme === 'coral-beach'
          ? '#f0ddbb'
          : palette[1];
    // A quiet runway separates physical pickups and coloured force lanes from
    // the saturated world scenery. Decorative side surfaces retain the theme.
    const roadColour = ['starlight-space', 'neon-city', 'moon-garden'].includes(level.theme)
      ? '#686f92'
      : ['underwater-reef', 'coral-beach', 'rainbow-waterfalls'].includes(level.theme)
        ? '#b6d6d4'
        : level.theme === 'golden-desert'
          ? '#e4d0ac'
          : '#d6dbe1';
    const dressing = new THREE.Group();
    factory.box(dressing, edgeColour, [0, -0.38, (start + end) / 2], [100, 0.5, end - start + 90]);
    factory.box(
      dressing,
      roadColour,
      [0, -0.23, (start + end) / 2],
      [width, 0.4, end - start + 70],
    );
    for (const side of [-1, 1]) {
      factory.box(
        dressing,
        city ? '#e6dfd2' : edgeColour,
        [side * 7.1, -0.005, (start + end) / 2],
        [4.2, 0.08, end - start + 70],
      );
      factory.box(
        dressing,
        '#f6e8ca',
        [side * 5.08, 0.055, (start + end) / 2],
        [0.18, 0.15, end - start + 70],
      );
    }
    for (let z = Math.floor(start / 2) * 2; z <= end; z += 6)
      factory.box(dressing, palette[2], [0, -0.018, z], [width * 0.97, 0.012, 0.035]);
    for (const platform of level.platforms.filter(inView)) {
      const mesh = factory.platform(platform, palette[1]);
      mesh.position.set(platform.x, platform.y, platform.z);
      dressing.add(mesh);
    }
    for (const pad of level.trampolines.filter(inView)) {
      const mesh = new THREE.Group();
      mesh.name = `trampoline:${pad.id}`;
      mesh.position.set(pad.x, pad.y, pad.z);
      factory.box(mesh, '#263456', [0, -0.15, 0], [pad.width, 0.2, pad.depth]);
      factory.box(mesh, '#65e8d4', [0, 0, 0], [pad.width * 0.85, 0.08, pad.depth * 0.94], 0.3);
      for (const z of [-1.6, -0.8, 0, 0.8, 1.6]) {
        for (const side of [-1, 1]) {
          const stripe = factory.box(
            mesh,
            '#fff6a5',
            [side * 0.18, 0.06, z],
            [0.1, 0.03, 0.6],
            0.2,
          );
          stripe.rotation.y = side * 0.65;
        }
      }
      for (const x of [-pad.width * 0.4, pad.width * 0.4])
        for (const z of [-pad.depth * 0.4, pad.depth * 0.4])
          for (const y of [-0.2, -0.12, -0.04]) {
            const coil = factory.torus(mesh, '#fff3ce', [x, y, z], 0.14);
            coil.rotation.x = Math.PI / 2;
          }
      // Dashed edge lanes lead into each pad; the elevated crate arc shows its reward.
      for (let z = -7; z < 0; z += 1.2)
        factory.box(mesh, '#fff6a5', [0, -pad.y + 0.03, z], [0.12, 0.02, 0.6], 0.1);
      root.add(mesh);
      springs.push({ mesh, pad });
    }
    const spacing = city ? 8 : 10;
    for (let row = Math.floor(start / spacing); row <= Math.ceil(end / spacing); row++)
      for (const side of [-1, 1]) {
        const index = Math.abs(row * 2 + (side === 1 ? 1 : 0));
        const mesh = factory.scenery(level.theme, index, palette);
        mesh.position.set(side * (city ? 9.9 : 8.2 + (index % 3) * 1.5), 0, row * spacing);
        if (city) mesh.rotation.y = (side * Math.PI) / 2;
        dressing.add(mesh);
      }
    for (const gate of (level.checkpoints || []).filter(inView)) {
      const mesh = checkpoint(THREE, factory, gate, level.bounds);
      root.add(mesh);
      gates.push(gate);
      // Independent materials prevent a nearby wall's fade leaking into other
      // gates or scenery using the factory's shared palette.
      const materials = new Map();
      mesh.traverse((node) => {
        if (!node.isMesh) return;
        if (!materials.has(node.material)) {
          const copy = factory.keep(node.material.clone());
          prepareMaterial(copy);
          materials.set(node.material, copy);
        }
        node.material = materials.get(node.material);
      });
      gateMeshes.push({ mesh, gate });
    }
    root.add(factory.batchScenery(dressing));
    foods = level.food.filter(inView).map((food) => {
      const index = level.food.indexOf(food);
      const mesh = factory.batchScenery(factory.food(food.kind, index));
      mesh.name = `food:${food.id}`;
      root.add(mesh);
      return { mesh, food };
    });
    hazards = level.hazards.filter(inView).map((hazard) => {
      const mesh = factory.batchScenery(factory.hazard(hazard.type, 0.5, hazard.behavior));
      mesh.name = `hazard:${hazard.id}`;
      mesh.scale.set(hazard.width, hazard.height, hazard.depth);
      root.add(mesh);
      return { mesh, id: hazard.id };
    });
    powers = (level.powerups || []).filter(inView).map((power) => {
      const mesh = pickup(THREE, factory, power.kind);
      mesh.name = `powerup:${power.id}`;
      root.add(mesh);
      return { mesh, power };
    });
    crates = level.crates.filter(inView).map((crate) => {
      const mesh = new THREE.Group();
      mesh.name = `crate:${crate.id}`;
      mesh.position.set(crate.x, crate.y, crate.z);
      const colour =
        crate.kind === 'spring'
          ? '#65e8d4'
          : crate.kind === 'reinforced'
            ? '#756289'
            : crate.routeId
              ? '#ffcf63'
              : '#bb7949';
      const shell = factory.box(mesh, colour, [0, 0, 0], [1, 1, 1], crate.routeId ? 0.15 : 0);
      shell.name = 'Procedural crate shell';
      for (const face of [-0.515, 0.515]) {
        for (const side of [-1, 1]) {
          // The imported star medallion replaces ordinary wooden bracing;
          // gameplay-specific stripes remain siblings of this fallback shell.
          const slat = factory.box(shell, '#fff3ce', [0, 0, face], [0.09, 1.12, 0.035]);
          slat.rotation.z = (side * Math.PI) / 4;
        }
        if (crate.points === 450)
          factory.box(mesh, '#65e8d4', [0, 0, face * 1.02], [0.26, 0.26, 0.04], 0.3);
      }
      if (crate.kind === 'reinforced')
        for (const x of [-0.3, 0.3])
          factory.box(mesh, '#ffce79', [x, 0, -0.55], [0.15, 1.05, 0.08]);
      if (crate.kind === 'spring')
        for (const y of [-0.3, 0, 0.3])
          factory.box(mesh, '#65e8d4', [0, y, -0.55], [0.75, 0.08, 0.08], 0.2);
      if (crate.routeId) factory.box(mesh, '#ffcf63', [0, 0.48, -0.55], [1.05, 0.07, 0.07], 0.15);
      root.add(mesh);
      detachCrates.push(crateArt.attach(shell));
      return { mesh, crate };
    });
    const material = factory.keep(
      new THREE.MeshPhysicalMaterial({
        color: '#81d9ff',
        transparent: true,
        opacity: 0.24,
        emissive: '#38bfff',
        emissiveIntensity: 0.6,
        roughness: 0.13,
        metalness: 0.04,
        clearcoat: 1,
        depthWrite: false,
      }),
    );
    prepareMaterial(material);
    shield = new THREE.Mesh(factory.keep(new THREE.SphereGeometry(1.05, 24, 16)), material);
    shield.name = 'Active single-hit shield';
    root.add(shield);
    shieldRim = factory.torus(root, '#a4f1ff', [0, 0, 0], 1.06, 1.2);
    shieldRim.name = 'Active shield outline';
    shieldRim.castShadow = false;
    boost = new THREE.Group();
    boost.name = 'Active boost slipstream';
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const streak = factory.box(
        boost,
        i % 2 ? '#fff5b0' : '#ffb536',
        [Math.cos(angle) * 0.65, Math.sin(angle) * 0.65, -0.8 - i * 0.12],
        [0.055, 0.055, 1.6],
        1.4,
      );
      streak.castShadow = false;
    }
    root.add(boost);
  }
  function update(state, dt, reduced, visualBob = state.bob, cameraPosition = null) {
    if (disposed) return;
    const nextSegment = Math.floor(state.bob.z / 24);
    if (segment !== nextSegment) build(nextSegment);
    const time = state.elapsed / 1000;
    for (const { mesh, pad } of springs) {
      const contact = state.events.some((e) => e.type === 'spring' && e.objectId === pad.id);
      if (contact) mesh.userData.contactAt = state.elapsed;
      const age = state.elapsed - (mesh.userData.contactAt ?? -1000);
      mesh.scale.y = reduced || age > 450 ? 1 : 1 + Math.sin((age / 450) * Math.PI * 2) * 0.45;
    }
    const collected = new Set(state.collected);
    for (const { mesh, food } of foods) {
      mesh.visible = !collected.has(food.id);
      mesh.position.set(
        food.x,
        food.y + (reduced ? 0 : Math.sin(time * 2.2 + food.z) * 0.08),
        food.z,
      );
      mesh.rotation.y = reduced ? 0 : time * 1.4;
    }
    const smashed = new Set(state.smashed);
    for (const { mesh, crate } of crates) mesh.visible = !smashed.has(crate.id);
    const positions = new Map(bobHazardsAt(state.level, state.elapsed).map((h) => [h.id, h]));
    for (const { mesh, id } of hazards) {
      const h = positions.get(id);
      if (h) mesh.position.set(h.x, h.y, h.z);
    }
    const used = new Set(state.powerupsCollected || []);
    for (const { mesh, power } of powers) {
      mesh.visible = !used.has(power.id);
      mesh.position.set(
        power.x,
        power.y + (reduced ? 0 : Math.sin(time * 2.6 + power.z) * 0.08),
        power.z,
      );
      mesh.rotation.y = reduced ? 0 : Math.sin(time * 1.4) * 0.25;
    }
    shield.visible = state.effects?.shieldCharges > 0 && state.effects.shieldUntil > state.elapsed;
    shield.position.set(visualBob.x, visualBob.y + 0.1, visualBob.z);
    shieldRim.visible = shield.visible;
    shieldRim.position.copy(shield.position);
    shieldRim.scale.setScalar(reduced ? 1.06 : 1.06 + Math.sin(time * 3) * 0.025);
    boost.visible = (state.effects?.boostUntil || 0) > state.elapsed;
    boost.position.set(visualBob.x, visualBob.y, visualBob.z);
    boost.rotation.z = reduced ? 0 : time * 0.8;
    boost.scale.z = reduced ? 1 : 1 + Math.sin(time * 8) * 0.12;
    // Fade before Bob crosses, stay ghosted until the actual trailing camera
    // clears the wall. Spatial smoothstep is frame-rate independent and also
    // works for a reduced-motion/static draw or retry without stale opacity.
    for (const { mesh, gate } of gateMeshes) {
      const approach = Math.max(0, Math.min(1, (visualBob.z - gate.z + 3) / 3));
      const clear = cameraPosition ? Math.max(0, Math.min(1, (cameraPosition.z - gate.z) / 2)) : 0;
      const smooth = (v) => v * v * (3 - 2 * v);
      const opacity = 1 - 0.94 * smooth(approach) * (1 - smooth(clear));
      mesh.traverse((node) => {
        if (!node.isMesh) return;
        const faded = opacity < 0.999;
        if (node.material.transparent !== faded) {
          node.material.transparent = faded;
          node.material.needsUpdate = true;
        }
        node.material.opacity = opacity;
        node.material.depthWrite = !faded;
        node.castShadow = !faded;
        node.receiveShadow = !faded;
      });
    }
    return { start: segment * 24 - 24, end: segment * 24 + 96, gates };
  }
  function dispose() {
    if (!disposed) {
      clear();
      crateArt.dispose();
      disposed = true;
    }
  }
  return {
    update,
    dispose,
    ready: crateArt.ready || Promise.resolve(false),
    get readiness() {
      return crateArt.status || 'fallback';
    },
  };
}
